import { useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { Loader2, Globe, Check, ShoppingCart, Briefcase, Layers } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const NORTH_STARS = [
  { v: 'launch', label: 'Launch', hint: 'Get to first revenue' },
  { v: 'scale', label: 'Scale', hint: 'Grow volume fast' },
  { v: 'roas', label: 'ROAS', hint: 'Efficiency on ad spend' },
  { v: 'profitability', label: 'Profitability', hint: 'Contribution margin' },
  { v: 'expansion', label: 'Expansion', hint: 'New markets or channels' },
];

const MODELS = [
  { v: 'd2c_ecommerce', label: 'E-commerce / D2C', icon: ShoppingCart, hint: 'ROAS, AOV, add-to-cart, LTV' },
  { v: 'b2b_leadgen', label: 'Lead gen / B2B', icon: Briefcase, hint: 'Leads, CPL, pipeline value' },
  { v: 'hybrid', label: 'Both', icon: Layers, hint: 'A mix of the two' },
];

const SOCIALS = [
  { k: 'meta', label: 'Facebook / Instagram' },
  { k: 'linkedin', label: 'LinkedIn' },
  { k: 'tiktok', label: 'TikTok' },
  { k: 'youtube', label: 'YouTube' },
];

export const BrandAuditStep = ({ isDark, value, onChange }) => {
  const [inferring, setInferring] = useState(false);
  const [inference, setInference] = useState(null);
  const lastUrl = useRef('');

  const set = useCallback((k, v) => onChange({ ...value, [k]: v }), [value, onChange]);

  const input = `w-full px-3 py-2.5 rounded-lg border text-sm outline-none transition-colors ${
    isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-100 focus:border-[#00D4FF]'
           : 'bg-white border-zinc-200 text-zinc-900 focus:border-[#002FA7]'}`;
  const lbl = `block text-xs font-semibold mb-1.5 ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`;

  /* Reads the site once the URL looks complete and proposes a business model.
     The user can always override it — we never lock them into a guess. */
  const runInference = useCallback(async url => {
    if (!url || url === lastUrl.current || !/\.[a-z]{2,}/i.test(url)) return;
    lastUrl.current = url;
    setInferring(true);
    try {
      const { data } = await axios.get(`${API}/api/brands/infer`, {
        params: { website_url: url }, withCredentials: true,
      });
      setInference(data);
      // Functional update: anything typed while the request was in flight survives.
      onChange(prev => (prev.business_model_locked
        ? prev
        : { ...prev, business_model: data.business_model }));
    } catch {
      setInference(null);
    } finally {
      setInferring(false);
    }
  }, [onChange]);

  useEffect(() => {
    const id = setTimeout(() => runInference((value.website_url || '').trim()), 900);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.website_url]);

  return (
    <div data-testid="onboarding-audit" className="space-y-5 max-h-[460px] overflow-y-auto pr-1">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className={lbl} htmlFor="ba-brand">Brand name</label>
          <input id="ba-brand" data-testid="audit-brand-name" className={input} placeholder="LuminoSkin"
            value={value.brand_name || ''} onChange={e => set('brand_name', e.target.value)} />
        </div>
        <div>
          <label className={lbl} htmlFor="ba-org">Your company or agency <span className="font-normal opacity-60">(optional)</span></label>
          <input id="ba-org" data-testid="audit-org-name" className={input} placeholder="Zenith Growth Partners"
            value={value.org_name || ''} onChange={e => set('org_name', e.target.value)} />
        </div>
      </div>

      <div>
        <label className={lbl} htmlFor="ba-url">Website</label>
        <div className="relative">
          <Globe size={15} className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`} />
          <input id="ba-url" data-testid="audit-website" className={input + ' pl-9'} placeholder="luminoskin.com"
            value={value.website_url || ''} onChange={e => set('website_url', e.target.value)} />
          {inferring && <Loader2 size={15} className="animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-[#00D4FF]" />}
        </div>
        {inference && (
          <p data-testid="audit-inference" className={`text-xs mt-1.5 ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>
            {inference.note ? inference.note : (
              <>We read your site and it looks like <strong>{MODELS.find(m => m.v === inference.business_model)?.label}</strong>
                {' '}({inference.confidence} confidence
                {inference.signals?.ecommerce?.length || inference.signals?.b2b?.length
                  ? `, from signals like "${[...(inference.signals.ecommerce || []), ...(inference.signals.b2b || [])].slice(0, 2).join('", "')}"`
                  : ''}). Change it below if that is wrong.</>
            )}
          </p>
        )}
      </div>

      <div>
        <span className={lbl}>Business model — this decides which metrics your dashboard shows</span>
        <div className="grid sm:grid-cols-3 gap-2">
          {MODELS.map(m => {
            const active = value.business_model === m.v;
            const Icon = m.icon;
            return (
              <button key={m.v} type="button" data-testid={`audit-model-${m.v}`}
                onClick={() => onChange({ ...value, business_model: m.v, business_model_locked: true })}
                className={`text-left px-3 py-2.5 rounded-lg border transition-all ${
                  active ? 'border-[#002FA7] bg-[#002FA7]/8'
                         : isDark ? 'border-zinc-700 hover:border-zinc-500' : 'border-zinc-200 hover:border-zinc-400'}`}>
                <span className="flex items-center gap-2 text-sm font-semibold">
                  <Icon size={15} color={active ? '#002FA7' : undefined} />{m.label}
                  {active && <Check size={13} className="ml-auto text-[#002FA7]" />}
                </span>
                <span className={`block text-xs mt-0.5 ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>{m.hint}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <div>
          <label className={lbl} htmlFor="ba-mrr">Current MRR</label>
          <input id="ba-mrr" data-testid="audit-mrr" className={input} type="number" min="0" placeholder="48000"
            value={value.current_mrr ?? ''} onChange={e => set('current_mrr', e.target.value)} />
        </div>
        <div>
          <label className={lbl} htmlFor="ba-roas">Current ROAS</label>
          <input id="ba-roas" data-testid="audit-roas" className={input} type="number" min="0" step="0.1" placeholder="3.4"
            value={value.current_roas ?? ''} onChange={e => set('current_roas', e.target.value)} />
        </div>
        <div>
          <label className={lbl} htmlFor="ba-budget">Target monthly budget</label>
          <input id="ba-budget" data-testid="audit-budget" className={input} type="number" min="0" placeholder="72000"
            value={value.target_budget ?? ''} onChange={e => set('target_budget', e.target.value)} />
        </div>
      </div>

      <div>
        <span className={lbl}>North star metric</span>
        <div className="flex flex-wrap gap-2">
          {NORTH_STARS.map(n => (
            <button key={n.v} type="button" data-testid={`audit-northstar-${n.v}`} onClick={() => set('north_star', n.v)}
              title={n.hint}
              className={`px-3.5 py-2 rounded-full border text-sm font-medium transition-all ${
                value.north_star === n.v ? 'border-[#002FA7] bg-[#002FA7] text-white'
                  : isDark ? 'border-zinc-700 text-zinc-300 hover:border-zinc-500' : 'border-zinc-200 text-zinc-700 hover:border-zinc-400'}`}>
              {n.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <span className={lbl}>Social profiles <span className="font-normal opacity-60">(optional)</span></span>
        <div className="grid sm:grid-cols-2 gap-2">
          {SOCIALS.map(s => (
            <input key={s.k} data-testid={`audit-social-${s.k}`} className={input} placeholder={s.label}
              value={value.social_urls?.[s.k] || ''}
              onChange={e => onChange({ ...value, social_urls: { ...(value.social_urls || {}), [s.k]: e.target.value } })} />
          ))}
        </div>
      </div>
    </div>
  );
};
